import { useEffect, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import Pill from '../../components/common/Pill';
import usePaginacao from '../../hooks/usePaginacao';
import { formatDateTime } from '../../utils/date';
import AbaDashboardAuditoria from './AbaDashboardAuditoria';
import * as auditoriaApi from '../../api/auditoria';

const TONE_ACAO = {
  create: 'success',
  update: 'neutral',
  delete: 'danger',
  resetar_senha: 'warning',
  redefinir_senha_esqueci: 'warning',
  login: 'success',
  login_falha: 'danger',
};

const LOTE = 500;

export default function Auditoria() {
  const [abaAtiva, setAbaAtiva] = useState('registros');
  const [logs, setLogs] = useState(null);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [entidade, setEntidade] = useState('');
  const [acao, setAcao] = useState('');
  const [temMaisAntigos, setTemMaisAntigos] = useState(false);
  const [carregandoMais, setCarregandoMais] = useState(false);

  const carregar = async () => {
    setErro('');
    try {
      const res = await auditoriaApi.listar(LOTE, 0);
      setLogs(res.data);
      setTemMaisAntigos(res.data.length === LOTE);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar a auditoria.'));
    }
  };

  const carregarMaisAntigos = async () => {
    setCarregandoMais(true);
    try {
      const res = await auditoriaApi.listar(LOTE, logs.length);
      setLogs((atual) => [...atual, ...res.data]);
      setTemMaisAntigos(res.data.length === LOTE);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar registros mais antigos.'));
    } finally {
      setCarregandoMais(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const buscaNormalizada = busca.trim().toLowerCase();
  const filtrados = (logs || []).filter((l) => {
    if (entidade && l.entidade !== entidade) return false;
    if (acao && l.acao !== acao) return false;
    if (
      buscaNormalizada &&
      !`${l.email || ''} ${l.descricao || ''}`.toLowerCase().includes(buscaNormalizada)
    )
      return false;
    return true;
  });

  const { paginados, Paginacao } = usePaginacao(filtrados, 25);

  const entidades = [...new Set((logs || []).map((l) => l.entidade))].sort();
  const acoes = [...new Set((logs || []).map((l) => l.acao))].sort();

  return (
    <div>
      <PageHeader
        titulo="Auditoria"
        subtitulo={logs ? `${filtrados.length} de ${logs.length} registro(s)` : undefined}
      />

      <div className="filter-tabs mb-3">
        <button
          type="button"
          className={`filter-tab${abaAtiva === 'registros' ? ' is-active' : ''}`}
          onClick={() => setAbaAtiva('registros')}
        >
          Registros
        </button>
        <button
          type="button"
          className={`filter-tab${abaAtiva === 'dashboard' ? ' is-active' : ''}`}
          onClick={() => setAbaAtiva('dashboard')}
        >
          Dashboard de Uso
        </button>
      </div>

      {abaAtiva === 'registros' &&
        (!logs ? (
          erro ? <ErroCarregamento mensagem={erro} onRetry={carregar} /> : <LoadingSpinner />
        ) : (
          <>
            <label className="am-field" style={{ maxWidth: 420, marginBottom: 18 }}>
              <div className="am-field__label-row">
                <span className="am-field__label">Buscar</span>
              </div>
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="E-mail ou descrição..." />
            </label>

            <div className="filtros-painel mb-3">
              <div className="filtros-painel__linha">
                <span className="filtros-painel__label">Entidade</span>
                <div className="filter-tabs">
                  <button
                    type="button"
                    className={`filter-tab${entidade === '' ? ' is-active' : ''}`}
                    onClick={() => setEntidade('')}
                  >
                    Todas
                  </button>
                  {entidades.map((e) => (
                    <button
                      key={e}
                      type="button"
                      className={`filter-tab${entidade === e ? ' is-active' : ''}`}
                      onClick={() => setEntidade(e)}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filtros-painel__linha">
                <span className="filtros-painel__label">Ação</span>
                <div className="filter-tabs">
                  <button
                    type="button"
                    className={`filter-tab${acao === '' ? ' is-active' : ''}`}
                    onClick={() => setAcao('')}
                  >
                    Todas
                  </button>
                  {acoes.map((a) => (
                    <button
                      key={a}
                      type="button"
                      className={`filter-tab${acao === a ? ' is-active' : ''}`}
                      onClick={() => setAcao(a)}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="card-soft" style={{ overflow: 'hidden' }}>
              {filtrados.length === 0 ? (
                <div className="empty-state">
                  <span className="ms">history</span>
                  <div>Nenhum registro de auditoria encontrado.</div>
                </div>
              ) : (
                <div className="am-table-wrap">
                  <table className="am-table">
                    <thead>
                      <tr>
                        <th>Data/hora</th>
                        <th>Usuário</th>
                        <th>Perfil</th>
                        <th>Entidade</th>
                        <th>Ação</th>
                        <th>Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginados.map((l) => (
                        <tr key={l.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(l.criado_em)}</td>
                          <td>{l.email || '—'}</td>
                          <td>{l.perfil || '—'}</td>
                          <td>{l.entidade}</td>
                          <td>
                            <Pill tone={TONE_ACAO[l.acao] || 'neutral'}>{l.acao}</Pill>
                          </td>
                          <td>{l.descricao || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <Paginacao />

            {temMaisAntigos && (
              <div className="d-flex justify-content-center mt-3">
                <button
                  type="button"
                  className="am-btn am-btn-outline"
                  disabled={carregandoMais}
                  onClick={carregarMaisAntigos}
                >
                  {carregandoMais ? 'Carregando...' : 'Carregar registros mais antigos'}
                </button>
              </div>
            )}
          </>
        ))}

      {abaAtiva === 'dashboard' && <AbaDashboardAuditoria />}
    </div>
  );
}
